import PolicyHolderImplementationStatusModel from '@/models/PolicyholderImplementationStatusModel.js';
import AffiliateDashboardStatsModel from '@/models/AffiliateDashboardStatsModel.js';
import BaseAPIClient from './base/BaseAPIClient';
import ListResponseModel from '../models/ListResponseModel';
import {PolicyholderActivityModel, PolicyholderActivityStatsModel} from '../models/PolicyholderActivityModel';

import TemplateLibraryMetricsModel from '../models/TemplateLibraryMetricsModel';
import PolicyholderTemplateActivityModel from '../models/PolicyholderTemplateActivityModel';

import PolicyholderDetailMetricsModel from '../models/PolicyholderDetailMetricsModel';
import PolicyholderLocationModel from '../models/PolicyholderLocationModel';
import PolicyholderEquipmentModel from '../models/PolicyholderEquipmentModel';
import PolicyholderTemplateModel from '../models/PolicyholderTemplateModel';
import { getAbbrMonthFromDate } from '../util.js';

export default class PreventClient extends BaseAPIClient {
  async getPolicyholderImplementationStatus(id, { search = '', ordering = '-id', limit = 50, offset = 0, stage__in = null } = {}) {
    const params = {
      search,
      ordering,
      limit,
      offset,
      stage__in,
    };
    const response = await this._get(`prevent/affiliates/${id}/policyholder_implementation_status/`, params);

    // Transform the response using models
    return {
      results: PolicyHolderImplementationStatusModel.createListFromResponse(response.results || []),
      stats: new AffiliateDashboardStatsModel(response),
    };
  }

  getPolicyholderActivity(affiliateId, {
    search,
    ordering,
    offset,
    limit,
  }) {
    const params = {
      search,
      ordering,
      offset,
      limit,
    };

    return this._get(`prevent/affiliates/${affiliateId}/activity/`, params).then(t => new ListResponseModel(t, PolicyholderActivityModel));
  }

  getPolicyholderActivityMetrics(affiliateId) {
    return this._get(`prevent/affiliates/${affiliateId}/metrics/activity/`).then(stats => new PolicyholderActivityStatsModel(stats));
  }

  getPolicyholderStats(affiliateId, policyholderId) {
    return this._get(`prevent/affiliates/${affiliateId}/policyholder/${policyholderId}/metrics/`).then(stats => new PolicyholderDetailMetricsModel(stats));
  }

  getPolicyholderLocations(affiliateId, policyholderId, {
    search,
    ordering,
    offset,
    limit,
  } = {}) {
    const params = {
      search,
      ordering,
      offset,
      limit,
    };

    return this._get(`prevent/affiliates/${affiliateId}/policyholder/${policyholderId}/location/`, params).then(data => new ListResponseModel(data, PolicyholderLocationModel));
  }

  getPolicyholderEquipment(affiliateId, policyholderId, {
    search,
    ordering,
    offset,
    limit,
  } = {}) {
    const params = {
      search,
      ordering,
      offset,
      limit,
    };

    return this._get(`prevent/affiliates/${affiliateId}/policyholder/${policyholderId}/equipment/`, params).then(data => new ListResponseModel(data, PolicyholderEquipmentModel));
  }

  getPolicyholderTemplates(affiliateId, policyholderId, {
    search,
    ordering,
    offset,
    limit,
  } = {}) {
    const params = {
      search,
      ordering,
      offset,
      limit,
    };

    return this._get(`prevent/affiliates/${affiliateId}/policyholder/${policyholderId}/template/`, params).then(data => new ListResponseModel(data, PolicyholderTemplateModel));
  }

  getAffiliatePolicyholderTemplates(affiliateId, {
    search,
    ordering,
    offset,
    limit,
  }) {
    const params = {
      search,
      ordering,
      offset,
      limit,
    };

    return this._get(`prevent/affiliates/${affiliateId}/template_library/`, params).then(t => new ListResponseModel(t, PolicyholderTemplateActivityModel));
  }

  getPolicyholderTemplateMetrics(affiliateId) {
    return this._get(`prevent/affiliates/${affiliateId}/metrics/template_library/`).then(m => new TemplateLibraryMetricsModel(m));
  }

  async getPolicyholderDetailsWorkRequestsSubmitted(affiliateId, policyholderId) {
    const response = await this._get(`prevent/affiliates/${affiliateId}/policyholder/${policyholderId}/charts/work_requests_submitted/`);
    response.results = response.results.map(item => ({
      ...item,
      month: getAbbrMonthFromDate(item.month),
    }));
    return response;
  }

  async getPolicyholderDetailsLoginChartData(affiliateId, policyholderId) {
    const response = await this._get(`prevent/affiliates/${affiliateId}/policyholder/${policyholderId}/charts/total_logins/`);
    response.results = response.results.map(item => ({
      ...item,
      month: getAbbrMonthFromDate(item.month),
    }));
    return response;
  }

  async getPolicyholderDetailsWorkOrdersCompletedData(affiliateId, policyholderId, {months = 6} = {}) {
    const params = {months};
    const response = await this._get(`prevent/affiliates/${affiliateId}/policyholder/${policyholderId}/charts/work_orders_completed/`, params);
    response.results = response.results.map(item => ({
      ...item,
      month: getAbbrMonthFromDate(item.month),
    }));
    return response;
  }
}
